// 用户模块
import { createSlice } from '@reduxjs/toolkit';
import { desensitizeEmail } from '../../utils/desensitizeEmail';
import authSession from '../../utils/authSession';
import { normalizeAccountModeration } from '../../utils/accountModeration';

const userStore = createSlice({
  name: 'user',
  initialState: {
    // Redux 只保存认证状态，不保存原始 token。
    isAuthenticated: false,
    userInfo: {},
    accountNotice: null,
    sessionVersion: 0,
    adminAccess: 'unknown',
    adminAccessError: '',
    adminCheckId: null,
  },
  reducers: {
    setAuthenticated(state, action) {
      state.sessionVersion += 1;
      state.adminAccess = 'unknown';
      state.adminAccessError = '';
      state.adminCheckId = null;
      state.userInfo = {};
      state.accountNotice = null;
      state.isAuthenticated = Boolean(action.payload);
    },
    setUserInfo(state, action) {
      if (state.userInfo.id && state.userInfo.id !== action.payload?.id) {
        state.sessionVersion += 1;
        state.adminAccess = 'unknown';
        state.adminAccessError = '';
        state.adminCheckId = null;
        state.accountNotice = null;
      }
      state.userInfo = {
        ...action.payload,
        email: desensitizeEmail(action.payload?.email),
      };
      if (Object.prototype.hasOwnProperty.call(action.payload || {}, 'account_moderation')) {
        state.accountNotice = normalizeAccountModeration(action.payload.account_moderation);
      }
    },
    setAccountNotice(state, action) {
      if (state.isAuthenticated) state.accountNotice = normalizeAccountModeration(action.payload);
    },
    clearUserState(state) {
      state.sessionVersion += 1;
      state.adminAccess = 'unknown';
      state.adminAccessError = '';
      state.adminCheckId = null;
      state.isAuthenticated = false;
      state.userInfo = {};
      state.accountNotice = null;
    },
    startAdminCheck(state, action) {
      state.adminAccess = 'checking';
      state.adminAccessError = '';
      state.adminCheckId = action.payload;
    },
    finishAdminCheck(state, action) {
      const { checkId, sessionVersion, userId, status, error, role, isAdmin } = action.payload;
      // 退出登录或切换账号后，旧请求不能重新授予管理员权限。
      if (!state.isAuthenticated || state.sessionVersion !== sessionVersion
        || state.userInfo.id !== userId || state.adminCheckId !== checkId) return;
      state.adminAccess = status;
      state.adminAccessError = error || '';
      state.adminCheckId = null;
      if (status === 'allowed' && ['admin', 'super_admin'].includes(role)) {
        state.userInfo.role = role;
        state.userInfo.is_admin = isAdmin;
        state.userInfo.is_superuser = role === 'super_admin';
      }
    },
    denyAdminAccess(state, action) {
      if (action.payload && (state.sessionVersion !== action.payload.sessionVersion
        || state.userInfo.id !== action.payload.userId)) return;
      state.adminAccess = 'denied';
      state.adminAccessError = '';
      state.adminCheckId = null;
    },
  },
});

const { setAuthenticated, setUserInfo, setAccountNotice, clearUserState, startAdminCheck, finishAdminCheck, denyAdminAccess } = userStore.actions;

export const selectIsAdmin = state => state.user.isAuthenticated && state.user.adminAccess === 'allowed';

const establishSession = tokens => dispatch => {
  authSession.setTokens(tokens);
  dispatch(setAuthenticated(Boolean(tokens?.access)));
  if (tokens?.access && tokens.account_notice) dispatch(setAccountNotice(tokens.account_notice));
};

const restoreSession = accessToken => dispatch => {
  authSession.setAccessToken(accessToken);
  dispatch(setAuthenticated(Boolean(accessToken)));
};

const logout = () => dispatch => {
  authSession.clear();
  dispatch(clearUserState());
};

const userReducer = userStore.reducer;

export { establishSession, restoreSession, setUserInfo, logout, startAdminCheck, finishAdminCheck, denyAdminAccess };

export default userReducer;
